import { IFile } from "../../components/UploadFile/types";
import { EducationType } from "../school/constants/types";
import { IRequiredDoc } from "../academic/types";
import { FC } from "react";
import { Routes } from "../../constants/routes";

export enum NoteType {
  AcademicDeactivation = 1,
  ProgramDeactivation = 2,
  IntakeDeactivation = 3,
  BranchDeactivation = 4,
  ApplicationRejection = 5,
  ApplicationCancellationByStudent = 6,
}

export interface INote {
  text: string;
  date: string;
  type: NoteType;
}

interface INoteBackEnd {
  created_at: string;
  text: string;
  type: NoteType;
}

export enum ApplicationStatus {
  PENDING = 1,
  APPROVED = 2,
  REJECTED = 3,
}

interface IApplicationStatusBackEndModel {
  type: ApplicationStatus;
  comment: string | null;
}

export const ApplicationStatusText = {
  [ApplicationStatus.REJECTED]: "Rejected",
  [ApplicationStatus.PENDING]: "Pending",
  [ApplicationStatus.APPROVED]: "Approved",
};

export const APPLICATION_STATUS = [
  {
    id: ApplicationStatus.PENDING,
    name: ApplicationStatusText[ApplicationStatus.PENDING],
  },
  {
    id: ApplicationStatus.APPROVED,
    name: ApplicationStatusText[ApplicationStatus.APPROVED],
  },
  {
    id: ApplicationStatus.REJECTED,
    name: ApplicationStatusText[ApplicationStatus.REJECTED],
  },
];

export interface IApplicationFrontEndModelStep2 {
  isLinkConfirmed: boolean;
  applicationFiles: {
    [key: string]: IFile;
  };
  requiredDocs: {
    [key: string]: IFile;
  };
  extraDocs: IFile[];
  comment: string;
}

export interface IApplicationFrontEndModelStep1 {
  branch: { branchId: string } | null;
  student: {
    id: string;
    studentProfile: {
      country: string;
    };
  } | null;
  intake: { id: string } | null;
}

export interface IApplicationFrontEndModel {
  injectedData: {
    programId: string;
    educationType: EducationType;
    program: {
      applicationLink: string;
      applicationForms: {
        id: string;
        name: string;
        path: string;
        size: number;
      }[];
      requiredDocs: {
        id: string;
        name: string;
      }[];
      branches: { branchId: string }[];
      intakes: {
        id: string;
        deadline: string;
        startDate: string;
      }[];
      restrictedCountries: string[];
    };
    isStudent: boolean;
    students: {
      id: string;
      name: string;
      studentProfile: {
        country: string;
      };
    }[];
  };
  step1: IApplicationFrontEndModelStep1;
  step2?: IApplicationFrontEndModelStep2;
}

export interface IApplicationBackEndModel {
  id?: string;
  programId?: string;
  courseId?: string;
  studentId: string;
  branchId: string;
  intakeId?: string;
  isLinkConfirmed: boolean;
  filledForms: {
    applicationFormId: string | undefined;
    fileId: string | undefined;
  }[];
  requiredDocs: {
    requiredDocId: string | undefined;
    fileId: string | undefined;
  }[];
  extraDocs: {
    fileId: string;
  }[];
  comment: string;
}

interface IValidatorParams {
  values: any;
  backendValidator?: (data?: any) => Promise<any>;
}

export type TAppValidator = (
  params: IValidatorParams,
) => Promise<void | Object>;

interface IStudentProgramApplicationBackEndModel {
  academic: {
    requiredDocs: IRequiredDoc[];
    school: {
      id: string;
      name: string;
      country: string;
      typeId: number;
      image: {
        preview: string;
      };
    };
    // for Higher
    degreeName?: string;
    // for Secondary
    levelName?: string;
    grade?: string;
    schoolTypeName?: string;
  };
  programType: number;
  programTypeId: number;
  id: string;
  currency: number;
  tuition: number;
  durationAmount: number;
  durationUnit: number;
  tuitionDurationAmount: number | null;
  tuitionDurationUnit: number;
  hasTuitionScholarship: boolean;
  hasTuitionFinancialAid: boolean;
  tuitionScholarshipType: number | null;
  tuitionScholarshipAmount: number | null;
  tuitionFinancialAidType: number | null;
  tuitionFinancialAidAmount: number | null;
  otherFeesScholarshipType: number | null;
  otherFeesScholarshipAmount: number | null;
  otherFeesFinancialAidType: number | null;
  otherFeesFinancialAidAmount: number | null;
  hasOtherFeesScholarship: boolean;
  hasOtherFeesFinancialAid: boolean;
  otherFeesTotal: number;
  majorName?: string;
}

interface IStudentLanguageCourseApplicationBackEndModel {
  id: string;
  school: {
    id: string;
    name: string;
    country: string;
    typeId: number;
    image: {
      preview: string;
    };
  };
  courseType: number;
  durationAmount: number;
  durationUnit: number;
  currency: number;
  priceDurationAmount: number | null;
  priceDurationUnit: number;
  price: number;
  otherFeesTotal: number;
  hasPriceDiscount: boolean;
  priceDiscountType: number | null;
  priceDiscountAmount: number | null;
  hasOtherFeesDiscount: boolean;
  otherFeesDiscountType: number | null;
  otherFeesDiscountAmount: number | null;
  category: string;
  language: string;
  requiredDocs: IRequiredDoc[];
}

interface ISchoolStudentApplicationProgramBackEndModel {
  academic: {
    requiredDocs: IRequiredDoc[];
    // for Higher
    degreeName?: string;
    // for Secondary
    levelName?: string;
    grade?: string;
    schoolTypeName?: string;
  };
  programType: number;
  currency: number;
  tuition: number;
  durationAmount: number;
  durationUnit: number;
  tuitionDurationAmount: number | null;
  tuitionDurationUnit: number;
  hasTuitionScholarship: boolean;
  hasTuitionFinancialAid: boolean;
  tuitionScholarshipType: number | null;
  tuitionScholarshipAmount: number | null;
  tuitionFinancialAidType: number | null;
  tuitionFinancialAidAmount: number | null;
  otherFeesScholarshipType: number | null;
  otherFeesScholarshipAmount: number | null;
  otherFeesFinancialAidType: number | null;
  otherFeesFinancialAidAmount: number | null;
  hasOtherFeesScholarship: boolean;
  hasOtherFeesFinancialAid: boolean;
  otherFeesTotal: number;
  majorName?: string;
  id: string;
}

interface ISchoolStudentApplicationLanguageCourseBackEndModel {
  id: string;
  courseType: number;
  durationAmount: number;
  durationUnit: number;
  currency: number;
  priceDurationAmount: number | null;
  priceDurationUnit: number;
  price: number;
  otherFeesTotal: number;
  hasPriceDiscount: boolean;
  priceDiscountType: number | null;
  priceDiscountAmount: number | null;
  hasOtherFeesDiscount: boolean;
  otherFeesDiscountType: number | null;
  otherFeesDiscountAmount: number | null;
  residenceAmount: number | null;
  homestayAmount: number | null;
  insuranceAmount: number | null;
  category: string;
  language: string;
  requiredDocs: IRequiredDoc[];
}

export interface IStudentApplicationCardProps {
  application: IStudentApplicationCardFrontEndModel;
  studentId: string;
}

export interface ISchoolStudentApplicationCardProps {
  application: ISchoolStudentApplicationCardFrontEndModel;
  schoolType: EducationType;
}

export interface IStudentApplicationCardHeaderContent {
  leftSection: {
    image: string;
    lRow0: {
      text0: string;
    };
    lRow1: {
      text0: string;
      text1?: string;
    };
    lRow2: {
      text0: string;
      text1?: string;
    };
  };
  rightSection: {
    rRow0: {
      text0: string;
      text0BackgroundColor: string;
    };
    rRow1?: {
      text0: string;
      text1?: string;
    };
  };
}

export interface ISchoolStudentApplicationCardHeaderContent {
  leftSection: {
    lRow0: {
      text0: string;
      text1?: string;
    };
    lRow2: {
      text0: string;
      text1?: string;
    };
  };
  rightSection: {
    rRow0: {
      text0: string;
      text0BackgroundColor: string;
    };
    rRow1?: {
      text0: string;
      text1?: string;
    };
  };
}

export interface IStudentApplicationCardCenterContent {
  id?: number;
  icon: FC;
  title: string;
  previewContent: FC | string[];
  expandedContent?: FC;
}

type CardFieldProps = {
  Icon?: FC;
  title: string;
  isExpanded?: boolean;
  PreviewContent: FC | string[] | undefined;
  onClickAway?: () => void;
} & (
  | {
      ExpandedContent: FC;
      onExpand: (any) => any;
      id: number;
    }
  | {
      ExpandedContent?: undefined;
      onExpand?: (any) => any;
      id?: number;
    }
);

export interface IStudentApplicationCardFrontEndModel {
  id: string;
  headerContent: IStudentApplicationCardHeaderContent;
  centerContent: IStudentApplicationCardCenterContent[];
  status: IApplicationStatusBackEndModel;
  schoolType: EducationType;
  schoolId: string;
  startDate?: string;
  submissionDate: string;
  schoolCancellationNote: INote | null;
  studentCancellationNote: INote | null;
  redirectRoute: Routes;
  entityName: string;
  entityId: string;
}

export interface ISchoolStudentApplicationCardFrontEndModel {
  id: string;

  headerContent: ISchoolStudentApplicationCardHeaderContent;
  centerContent: IStudentApplicationCardCenterContent[];
  status: IApplicationStatusBackEndModel;
  schoolType: EducationType;
  entityId: string;
  entityName: string;
  startDate?: string;
  submissionDate: string;
  schoolCancellationNote: INote | null;
  studentCancellationNote: INote | null;
}

export interface IStudentProgramApplicationCardBackEndModel {
  id: string;
  program: IStudentProgramApplicationBackEndModel;
  created_at: string;
  intake: {
    startDate: string;
    deadline: string;
  };
  schoolCancellationNote: {
    created_at: string;
    text: string;
    type: NoteType;
  } | null;
  studentCancellationNote: {
    created_at: string;
    text: string;
    type: NoteType;
  } | null;
  status: IApplicationStatusBackEndModel;
}

export interface IStudentLanguageCourseApplicationCardBackEndModel {
  id: string;
  course: IStudentLanguageCourseApplicationBackEndModel;
  created_at: string;
  branch: {
    country: string;
  };
  schoolCancellationNote: {
    created_at: string;
    text: string;
    type: NoteType;
  } | null;
  studentCancellationNote: {
    created_at: string;
    text: string;
    type: NoteType;
  } | null;
  status: IApplicationStatusBackEndModel;
}

export interface ISchoolStudentApplicationCardBackEndModel {
  id: string;
  branch: {
    name: string;
  };
  program: ISchoolStudentApplicationProgramBackEndModel;
  created_at: string;
  isDisabled: boolean;
  intake: {
    startDate: string;
    deadline: string;
  };
  schoolCancellationNote: INoteBackEnd | null;
  studentCancellationNote: INoteBackEnd | null;
  status: IApplicationStatusBackEndModel;
}

export interface ISchoolStudentApplicationCardLanguageCourseBackEndModel {
  id: string;
  branch: {
    name: string;
  };
  status: IApplicationStatusBackEndModel;
  schoolCancellationNote: INoteBackEnd | null;
  studentCancellationNote: INoteBackEnd | null;
  created_at: string;
  course: ISchoolStudentApplicationLanguageCourseBackEndModel;
}

export interface IApplicationStatusProps {
  status: IApplicationStatusBackEndModel;
}

export interface IApplicationStatusControlProps {
  status: ApplicationStatus;
  id: string;
  schoolType: EducationType;
}

export interface IProgramApplicationViewFrontEndModel {
  injectedData: {
    educationType: EducationType;
    student: {
      id: string;
      name: string;
    };
    branch: {
      name: string;
      id: string;
    };
    program: {
      academic: {
        applicationForms?: {
          id: string;
          name: string;
          path: string;
          size: number;
        }[];
        applicationLink: string;
        requiredDocs: {
          id: string;
          name: string;
        }[];
      };
      intakes: {
        deadline: string;
        id: string;
        startDate: string;
      }[];
    };
    requiredDocs:
      | {
          received: {
            id: string;
            name: string | undefined;
          };
          filled:
            | {
                requiredDocId: string;
                file: {
                  id: string;
                  name: string;
                  preview: string;
                  size: number;
                };
              }
            | undefined;
        }[]
      | undefined;
    filledForms:
      | {
          received: {
            id: string;
            name: string;
            path: string;
            size: number;
          };
          filled:
            | {
                applicationFormId: string;
                file: {
                  id: string;
                  name: string;
                  preview: string;
                  size: number;
                };
              }
            | undefined;
        }[]
      | undefined;
    extraDocs: {
      id: string;
      name: string;
      preview: string;
      size: number;
    }[];
    comment: string;
  };
  step1: {
    isLinkConfirmed: boolean;
    intake: {
      id: string;
      deadline: string;
      startDate: string;
      name: string;
    } | null;
  };
  selectedIntake: {
    id: string;
    name: string;
    deadline: string;
  };
}

export interface ILanguageCourseApplicationViewFrontEndModel {
  injectedData: {
    educationType: EducationType;
    student: {
      id: string;
      name: string;
    };
    branch: {
      name: string;
      id: string;
    };
    course: {
      applicationForms?: {
        id: string;
        name: string;
        path: string;
        size: number;
      }[];
      applicationLink: string;
      requiredDocs: {
        id: string;
        name: string;
      }[];
    };
    requiredDocs:
      | {
          received: {
            id: string;
            name: string | undefined;
          };
          filled:
            | {
                requiredDocId: string;
                file: {
                  id: string;
                  name: string;
                  preview: string;
                  size: number;
                };
              }
            | undefined;
        }[]
      | undefined;
    filledForms:
      | {
          received: {
            id: string;
            name: string;
            path: string;
            size: number;
          };
          filled:
            | {
                applicationFormId: string;
                file: {
                  id: string;
                  name: string;
                  preview: string;
                  size: number;
                };
              }
            | undefined;
        }[]
      | undefined;
    extraDocs: {
      id: string;
      name: string;
      preview: string;
      size: number;
    }[];
    comment: string;
  };
  step1: {
    isLinkConfirmed: boolean;
  };
}

interface IApplicationViewBackEndModel {
  student: {
    id: string;
    name: string;
  };
  branch: { name: string; id: string };
  isLinkConfirmed: boolean;
  extraDocs: {
    file: {
      id: string;
      name: string;
      preview: string;
      size: number;
    };
  }[];
  requiredDocs: {
    requiredDocId: string;
    file: {
      id: string;
      name: string;
      preview: string;
      size: number;
    };
  }[];
  filledForms: {
    applicationFormId: string;
    file: {
      id: string;
      name: string;
      preview: string;
      size: number;
    };
  }[];
  comment: string;
}

export interface IProgramApplicationViewBackEndModel
  extends IApplicationViewBackEndModel {
  intake: {
    startDate: string;
    id: string;
    deadline: string;
  };
  program: {
    academic: {
      applicationForms: {
        id: string;
        name: string;
        path: string;
        size: number;
      }[];
      applicationLink: string;
      requiredDocs: {
        id: string;
        name: string;
      }[];
    };
    intakes: {
      deadline: string;
      id: string;
      startDate: string;
    }[];
  };
}

export interface ILanguageCourseApplicationViewBackEndModel
  extends IApplicationViewBackEndModel {
  course: {
    applicationForms: {
      id: string;
      name: string;
      path: string;
      size: number;
    }[];
    applicationLink: string;
    requiredDocs: {
      id: string;
      name: string;
    }[];
  };
}

export interface IApplicationEditFrontEndModel {
  id: string;
  injectedData: {
    educationType: EducationType;
    branch: {
      name: string;
      id: string;
    };
    comment: string;
    intake: string;
    isLinkConfirmed: boolean;
    program?: {
      academic: {
        applicationForms?: {
          id: string;
          name: string;
          path: string;
          size: number;
        }[];
        applicationLink: string;
        requiredDocs: {
          id: string;
          name: string;
        }[];
      };
    };
    course?: {
      applicationForms?: {
        id: string;
        name: string;
        path: string;
        size: number;
      }[];
      applicationLink: string;
      requiredDocs: {
        id: string;
        name: string;
      }[];
    };
    extraDocs: {
      id: string;
      name: string;
      size: number;
      preview?: string;
      isRemovable: boolean;
    }[];
    filledForms:
      | {
          received: {
            id: string;
            name: string;
            path: string;
            size: number;
          };
          filled:
            | {
                applicationFormId: string;
                file: {
                  id: string;
                  name: string;
                  preview: string;
                  size: number;
                };
              }
            | undefined;
        }[]
      | undefined;
    requiredDocs:
      | {
          received: {
            id: string;
            name: string | undefined;
          };
          filled:
            | {
                requiredDocId: string;
                file: {
                  id: string;
                  name: string;
                  preview: string;
                  size: number;
                };
              }
            | undefined;
        }[]
      | undefined;
    student: {
      id: string;
      name: string;
    };
  };
  step1: {
    intake: {
      name: string;
      id: string;
      deadline: string;
    };
    isLinkConfirmed: boolean;
    requiredDocs?: {
      [key: string]: IFile;
    };
    applicationFiles?: {
      [key: string]: IFile;
    };
  };
}
